
/* ==========================================================================
   ACTIVE BUILD TELEMETRY PANEL RENDERER (/database/js/telemetry-panel.js)
   ========================================================================== */

import { getCaseModeSelection } from '../state.js?v=20260819-audit2';
import { stableKey } from '../runtime.js?v=20260819-audit2';
import { escapeHtml, formatCaseBrandTitle, sanitizeUrlValue } from '../utils.js?v=20260819-audit2';
import { getEffectiveSpecs } from '../compatibility-engine.js?v=20260819-audit2';
import { renderPopularHardwarePanel, renderHardwareRecommendations } from './hardware-insights.js?v=20260819-phase35';

let lastRecommendationPanelKey = '';

export function renderTelemetryPanel(caseObj, evalResult, formInputs, aiResult = null) {
  const container = document.getElementById('sffActiveTelemetry') 
                 || document.getElementById('sffTelemetryPanel') 
                 || document.getElementById('sff-telemetry-box') 
                 || document.getElementById('telemetryContainer');
  if (!container) return;

  if (!caseObj || !evalResult || !formInputs) {
    container.innerHTML = `
      <div class="telemetry-empty-state font-mono">
        <span>NO CASE SELECTED</span>
        <p>Click <strong>validate</strong> on any case card to inspect telemetry and live spatial deltas.</p>
      </div>`;
    renderPopularHardwarePanel(null, null);
    renderHardwareRecommendations(null, null, null);
    return;
  }

  const { fits, warns = [], fails = [], unknowns = [], spineStep, activeSpecs } = evalResult;
  const effSpecs = activeSpecs || getEffectiveSpecs(caseObj, getCaseModeSelection(caseObj, 'default'));

  const userGpuL = formInputs.gpuL || 304;
  const userGpuT = formInputs.gpuT || 40;
  const userCpuH = formInputs.cpuH || 47;
  const userRad  = formInputs.rad || 240;
  const isLiquid = formInputs.cooling === 'liquid';

  // evalResult.activeSpecs is the authoritative post-mode, post-spine,
  // post-dynamic-rule constraint object used by the compatibility verdict.
  const maxCpu = effSpecs.maxCPU;
  const maxGpu = effSpecs.maxGPU;
  const maxThick = effSpecs.maxGPUT;
  const maxAio = effSpecs.maxAIO;

  const knownSpecs = effSpecs.known || {};
  const cpuDelta = !knownSpecs.maxCPU ? 'UNKNOWN' : (maxCpu >= 9999 ? 'OPEN' : (maxCpu - userCpuH));
  const gpuLDelta = !knownSpecs.maxGPU ? 'UNKNOWN' : (maxGpu >= 9999 ? 'OPEN' : (maxGpu - userGpuL));
  const gpuTDelta = !knownSpecs.maxGPUT ? 'UNKNOWN' : (maxThick >= 9999 ? 'OPEN' : (maxThick - userGpuT));

  const formatDeltaPill = (val) => {
    if (val === 'OPEN') return `<span class="audit-pill pill-sky">UNRESTRICTED</span>`;
    if (val === 'UNKNOWN') return `<span class="audit-pill pill-amber">VERIFY / UNKNOWN</span>`;
    if (val >= 0) return `<span class="audit-pill pill-emerald" style="background:rgba(16,185,129,0.15);color:#10b981;">+${val}mm Headroom</span>`;
    return `<span class="audit-pill pill-rose" style="background:rgba(244,63,94,0.15);color:#f43f5e;">${val}mm Conflict</span>`;
  };

  const statusBadge = fits 
    ? ((warns.length || unknowns.length) ? `<span class="audit-status-badge badge-amber">VERIFY ⚠</span>` : `<span class="audit-status-badge badge-emerald">CLEARANCE PASS ✓</span>`)
    : `<span class="audit-status-badge badge-rose">FAILED ❌</span>`;

  const diagnosticBlocks = [];
  if (fails.length > 0) {
    diagnosticBlocks.push(`<div class="telemetry-diagnostic telemetry-diagnostic-fail"><strong class="telemetry-diagnostic-title">FAILURES (${fails.length})</strong><ul class="telemetry-diagnostic-list">${fails.map(f => `<li>${escapeHtml(f)}</li>`).join('')}</ul></div>`);
  }
  if (warns.length > 0) {
    diagnosticBlocks.push(`<div class="telemetry-diagnostic telemetry-diagnostic-warn"><strong class="telemetry-diagnostic-title">WARNINGS (${warns.length})</strong><ul class="telemetry-diagnostic-list">${warns.map(w => `<li>${escapeHtml(w)}</li>`).join('')}</ul></div>`);
  }
  if (unknowns.length > 0) {
    diagnosticBlocks.push(`<div class="telemetry-diagnostic telemetry-diagnostic-unknown"><strong class="telemetry-diagnostic-title">UNKNOWN DATA (${unknowns.length})</strong><ul class="telemetry-diagnostic-list">${unknowns.map(w => `<li>${escapeHtml(w)}</li>`).join('')}</ul></div>`);
  }
  const diagnosticsHtml = diagnosticBlocks.join('');

  const effectiveAi = aiResult || evalResult.aiResult || null;
  let coreAiHtml = '';
  let cableBadgeHtml = '';

  if (effectiveAi) {
    const authorityColor = effectiveAi.fits ? '#10b981' : '#f43f5e';
    const difficultyLabels = ['EASY', 'MODERATE', 'CHALLENGING', 'HARD', 'EXTREME'];
    const difficultyText = difficultyLabels[(effectiveAi.difficultyRating || 1) - 1] || 'MODERATE';
    const hasResidualRisk = effectiveAi.modelAvailable !== false && Number.isFinite(effectiveAi.residualRiskScore);
    const riskLevel = effectiveAi.residualRiskLevel || 'LOW';
    const riskColor = riskLevel === 'HIGH' ? '#f43f5e' : (riskLevel === 'MODERATE' ? '#fbbf24' : '#10b981');

    const authorityBadge = `<div class="font-mono telemetry-info-row"><span class="telemetry-info-label">Compatibility Authority:</span><span class="telemetry-info-value" style="color:${authorityColor};">DETERMINISTIC ENGINE (${effectiveAi.fits ? 'FIT' : 'NO FIT'})</span></div>`;
    const residualBadge = hasResidualRisk
      ? `<div class="font-mono telemetry-info-row"><span class="telemetry-info-label">Edge-AI Residual Issue Risk:</span><span class="telemetry-info-value" style="color:${riskColor};">${effectiveAi.residualRiskScore}/100 (${riskLevel})</span></div>`
      : '';
    const difficultyBadge = `<div class="font-mono telemetry-info-row"><span class="telemetry-info-label">Assembly Difficulty:</span><span class="telemetry-info-value" style="color:#38bdf8;">${effectiveAi.difficultyRating}/5 (${difficultyText})</span></div>`;

    coreAiHtml = `${authorityBadge}${residualBadge}${difficultyBadge}`;
    cableBadgeHtml = effectiveAi.cableRiskDetected
      ? `<div class="font-mono telemetry-cable-risk">⚠ CABLE RISK: HIGH (TIGHT BEND / CROWDING)</div>`
      : '';
  }

  const buildEvidence = evalResult.buildEvidence || null;
  let buildEvidenceHtml = '';
  if (buildEvidence) {
    const bandLabels = {
      EXACT: 'EXACT MATCH',
      STRONG: 'STRONG',
      MODERATE: 'MODERATE',
      RELATED: 'RELATED',
      CASE_ONLY: 'CASE EVIDENCE',
      NONE: 'NONE YET'
    };
    const bandColors = {
      EXACT: '#10b981', STRONG: '#10b981', MODERATE: '#38bdf8',
      RELATED: '#fbbf24', CASE_ONLY: '#a1a1aa', NONE: '#71717a'
    };
    const band = buildEvidence.band || 'NONE';
    const best = buildEvidence.bestMatch || null;
    const total = Number(buildEvidence.totalCaseBuilds || 0);
    const eligible = Number(buildEvidence.evidenceEligibleBuilds || 0);
    const documentaryPhotos = Number(buildEvidence.documentaryPhotoCount || 0);
    const comparable = Number(buildEvidence.selectedComparableParts || 0);

    let evidenceDetail = '';
    if (best && best.matchedCount > 0) {
      const labels = Array.isArray(best.matchedLabels) ? best.matchedLabels.join(' + ') : '';
      const safeHref = best.permalink ? sanitizeUrlValue(best.permalink) : '#';
      const link = safeHref && safeHref !== '#'
        ? `<a href="${escapeHtml(safeHref)}" target="_blank" rel="noopener noreferrer" style="color:#38bdf8;text-decoration:none;">${escapeHtml(best.title || 'View build')}</a>`
        : escapeHtml(best.title || 'Documented build');
      const bestPhotoCount = Number(best.photoCount || 0);
      evidenceDetail = `<div class="font-mono telemetry-evidence-detail">Best documented match: ${best.matchedCount}/${best.comparedCount} comparable selections${labels ? ` · ${escapeHtml(labels)}` : ''}${bestPhotoCount > 0 ? ` · ${bestPhotoCount} photo${bestPhotoCount === 1 ? '' : 's'}` : ''} · ${link}</div>`;
    } else if (total > 0 && comparable === 0) {
      evidenceDetail = `<div class="font-mono telemetry-evidence-detail">${total} published build${total === 1 ? '' : 's'} documented for this case. Select catalog hardware to compare configuration similarity.</div>`;
    } else if (total > 0) {
      evidenceDetail = `<div class="font-mono telemetry-evidence-detail">${total} published build${total === 1 ? '' : 's'} documented for this case; no close match in the current evidence window.</div>`;
    }

    buildEvidenceHtml = `
      <div class="telemetry-evidence-card">
        <div class="font-mono telemetry-info-row telemetry-evidence-summary">
          <span class="telemetry-info-label">Documented Build Evidence:</span>
          <span class="telemetry-info-value" style="color:${bandColors[band] || '#a1a1aa'};">${bandLabels[band] || band}${eligible > 0 ? ` · ${eligible} DOCUMENTED BUILD${eligible === 1 ? '' : 'S'}${documentaryPhotos > 0 ? ` · ${documentaryPhotos} PHOTO${documentaryPhotos === 1 ? '' : 'S'}` : ''}` : ''}</span>
        </div>
        ${evidenceDetail}
      </div>`;
  }

  const gnnResult = evalResult.gnnResult || (Array.isArray(evalResult.gnnDeltas) ? {
    deltas: evalResult.gnnDeltas,
    interactions: evalResult.gnnInteractions || [],
    constraintEffects: evalResult.gnnConstraintEffects || []
  } : null);
  let gnnBadgeHtml = '';

  // GNN V4 is intentionally advisory and independent from the deterministic engine.
  // Surface only cross-component relations on an otherwise clean deterministic result;
  // known failures/warnings already have source-grounded messages and should not be duplicated.
  if (gnnResult && Array.isArray(gnnResult.interactions) && gnnResult.interactions.length >= 36 &&
      evalResult.fits && (!Array.isArray(evalResult.warns) || evalResult.warns.length === 0)) {
    const interactions = gnnResult.interactions;
    const effects = Array.isArray(gnnResult.constraintEffects) ? gnnResult.constraintEffects : [];
    const coolingRaw = String(formInputs.cooling || 'air').toLowerCase();
    const liquid = coolingRaw.includes('liquid') || coolingRaw.includes('aio') || coolingRaw.includes('water');
    const nodeNames = ['CASE', 'MOTHERBOARD', 'GPU', 'PSU', 'CPU COOLER', 'AIO'];
    const propertyNames = ['GPU LENGTH', 'GPU HEIGHT', 'GPU THICKNESS', 'CPU COOLER', 'AIO', 'PSU LENGTH'];
    const threshold = 0.72;
    const candidates = [
      [1, 3], // motherboard ↔ PSU
      [2, 3], // GPU ↔ PSU
      [1, 2], // motherboard ↔ GPU
      ...(liquid ? [[1, 5], [2, 5], [3, 5]] : [[1, 4], [2, 4], [3, 4]])
    ];

    const relationRows = [];
    for (const [a, b] of candidates) {
      const score = Number(interactions[a * 6 + b] || 0);
      if (!Number.isFinite(score) || score < threshold) continue;

      let bestEffect = null;
      for (const [src, dst] of [[a, b], [b, a]]) {
        for (let p = 0; p < 6; p++) {
          const value = Number(effects[((src * 6 + dst) * 6) + p] || 0);
          if (!Number.isFinite(value) || Math.abs(value) < 5) continue;
          if (!bestEffect || Math.abs(value) > Math.abs(bestEffect.value)) {
            bestEffect = { value, property: propertyNames[p] };
          }
        }
      }

      relationRows.push({
        score,
        label: `${nodeNames[a]} ↔ ${nodeNames[b]}`,
        effect: bestEffect
      });
    }

    relationRows.sort((a, b) => b.score - a.score);
    if (relationRows.length > 0) {
      gnnBadgeHtml = relationRows.slice(0, 2).map(row => {
        const effectText = row.effect
          ? ` · ${escapeHtml(row.effect.property)} ENVELOPE ${row.effect.value > 0 ? '+' : ''}${Math.round(row.effect.value)}MM`
          : '';
        return `<div class="font-mono telemetry-relational-row">RELATIONAL AI: ${escapeHtml(row.label)} ${(row.score * 100).toFixed(0)}%${effectText}</div>`;
      }).join('');
    }
  }

  const activeModeId = getCaseModeSelection(caseObj, 'default');
  const activeModeObj = Array.isArray(caseObj.modes) ? caseObj.modes.find(m => m.mode_id === activeModeId) : null;
  const activeModeTitle = activeModeObj ? (activeModeObj.mode_name || activeModeObj.mode_id) : null;

  container.innerHTML = `
    <div class="font-mono telemetry-case-header">
      <strong class="telemetry-case-title">${escapeHtml(formatCaseBrandTitle(caseObj.name))}</strong>
      <div class="telemetry-case-subrow">
        <span class="telemetry-case-meta">${caseObj.volume}L · ${escapeHtml(String(caseObj.layout).toUpperCase())}</span>
        ${statusBadge}
      </div>
    </div>

    ${activeModeTitle ? `<div class="font-mono telemetry-mode-row telemetry-mode-active">MODE: ${escapeHtml(activeModeTitle.toUpperCase())}</div>` : ''}
    ${spineStep ? `<div class="font-mono telemetry-mode-row telemetry-spine-active">SPINE MODE: ${escapeHtml(spineStep.slotName.toUpperCase())}</div>` : ''}

    <div class="font-mono telemetry-clearance-list">
      <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px dashed var(--sff-border-subtle);padding-bottom:3px;">
        <span style="color:var(--sff-text-muted);">GPU Length:</span>
        ${formatDeltaPill(gpuLDelta)}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px dashed var(--sff-border-subtle);padding-bottom:3px;">
        <span style="color:var(--sff-text-muted);">GPU Thickness:</span>
        ${formatDeltaPill(gpuTDelta)}
      </div>
      <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px dashed var(--sff-border-subtle);padding-bottom:3px;">
        <span style="color:var(--sff-text-muted);">CPU Cooler:</span>
        ${formatDeltaPill(cpuDelta)}
      </div>
      ${isLiquid ? `
      <div style="display:flex;justify-content:space-between;align-items:center;border-bottom:1px dashed var(--sff-border-subtle);padding-bottom:3px;">
        <span style="color:var(--sff-text-muted);">AIO Support:</span>
        <span style="font-weight:700;color:${!knownSpecs.maxAIO ? '#38bdf8' : (maxAio >= userRad ? '#10b981' : '#f43f5e')};">${!knownSpecs.maxAIO ? 'UNKNOWN / VERIFY' : (maxAio >= 9999 ? 'OPEN' : maxAio + 'mm Max')} (${userRad}mm)</span>
      </div>` : ''}
    </div>

    <div id="aiInferenceBadgeContainer" style="margin-top:6px;display:flex;flex-direction:column;gap:4px;">
      ${coreAiHtml}
      ${buildEvidenceHtml}
      ${cableBadgeHtml}
      ${gnnBadgeHtml}
      ${diagnosticsHtml}
    </div>
  `;

  const recommendationKey = stableKey([
    caseObj._stableKey || caseObj.name,
    getCaseModeSelection(caseObj, 'default'),
    formInputs.gpuL, formInputs.gpuT, formInputs.gpuH, formInputs.pcie,
    formInputs.cpuH, formInputs.cooling, formInputs.rad, formInputs.radFan,
    formInputs.mobo, formInputs.psu,
    evalResult.activeSpecs?.maxGPU, evalResult.activeSpecs?.maxGPUT,
    evalResult.activeSpecs?.maxGPUH, evalResult.activeSpecs?.maxCPU,
    evalResult.activeSpecs?.maxAIO, evalResult.activeSpecs?.maxPSU
  ]);
  if (recommendationKey !== lastRecommendationPanelKey) {
    lastRecommendationPanelKey = recommendationKey;
    renderPopularHardwarePanel(caseObj, formInputs);
    renderHardwareRecommendations(caseObj, formInputs, evalResult);
  }
}
